import { createStackNavigator } from "@react-navigation/stack";
import { DashboardNavigatorParamList } from "../types";
import React, { memo, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { CONTEXT_SIZE_TOGGLE, HIDE_NOTIFICATIONS_MODAL, NAVIGATE_TAB, SET_SALES_DATE_FILTER, SHOW_AUTH_MODAL, SHOW_USER_CONTEXT_MODAL, TOGGLE_GLOBAL_HIERARCHY, UPDATE_HELD_PROMO_CODE } from "@reduxLocal/actions/types";
import { DataNavigator, DataStack } from "./DataStack";
import { MailNavigator, MailStack } from "./MailStack";
import { SalesNavigator, SalesStack } from "./SalesStack";
import { AcademyNavigator, AcademyStack } from "./AcademyStack";
import { SettingsNavigator, SettingsStack } from "./SettingsStack";
import { UserNavigator, UserStack } from "./UserStack";
import WelcomeScreen from "../screens/MidLevel/WelcomeScreen/WelcomeScreen";
import { NetworkNavigator } from "./NetworkingStack";
import NetworkingStack from "./NetworkingStack";
import { ProjectManagementNavigator, ProjectManagementStack } from "./ProjectStack";
import IntegrationScreen from "../screens/MidLevel/IntegrationsScreen/IntegrationScreen";
import HelpScreen from "../screens/MidLevel/HelpScreen";
import ComingSoonScreen from '../screens/NonMenu/ComingSoonScreen';
import Community from "../screens/MidLevel/CommunityScreen/Community";
import { MessageNavigator, MessageStack } from "./MessageStack";
import { ReportNavigator, ReportStack } from "./ReportsStack";
import { KnowledgeNavigator, KnowledgeStack } from "./KnowledgeStack";
import { CalendarStack, CalendarStackNavigator } from "./CalendarStack";
import { CalendarHeader } from "./CalendarHeader";
import { DefaultRootState, store } from "@reduxLocal/persistState";
import InvitationsScreen from "../screens/MidLevel/DataScreen/InvitationsScreen";
import { useDispatch, useSelector } from "react-redux";
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { ToolbarContext } from "@hooks/toolbarContext";
import { googleAuths } from "@reduxLocal/selectors/userSelectors";
import { useQuery } from "@tanstack/react-query";
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import apis from "@api/api";
import { useRoute } from "@react-navigation/native";
import { FlowStack, FlowNavigator } from "./FlowStack";
import AddCompanyModal from "@components/modals/AddCompanyModal/AddCompanyModal";
import getEmailCount from "@utils/getEmailCount";

const DashStack = createStackNavigator<DashboardNavigatorParamList>();

const ConnectedHeader = () => {
  const { hierarchySocket } = useContext(ToolbarContext);
  const [connected, setConnected] = useState(false);
  const theme = themeSelector();

  useEffect(() => {
    if (hierarchySocket?.connected) {
      setConnected(true);
    }
  }, [hierarchySocket])

  return (
    <MaterialCommunityIcons name={connected ? "access-point-network" : "access-point-network-off"} size={24} color={theme ? "black" : "white"} />
  )
}

const DashboardStack = () => {
 
  const dispatch = useDispatch();
// Google Authentication data
  const googleObject = googleAuths();
  const theme = themeSelector();
  const googleId = useMemo(() => googleObject?.googleId, [googleObject?.googleId]);
  const googleToken = useMemo(() => googleObject?.accessToken, [googleObject?.accessToken]);
  const focusMode = useSelector((state: DefaultRootState) => state.UI.focusMode);


  const route = useRoute();

  // Use useEffect to handle route params changes more efficiently
  useEffect(() => {
    const routeCode = (route?.params as any)?.code;
    
    if (routeCode) {
      // Instrumentation only - no console logs
      dispatch({ type: UPDATE_HELD_PROMO_CODE, payload: routeCode });
    }
  }, [route?.params, dispatch]); // Only depend on route.params and dispatch

  // Query for integration data
  const getIntegrations = useCallback(async () => {
    const responseData = await apis.getIntegrations();
    return responseData;
  }, []);

  useQuery({
    queryKey: ['integrationData'],
    queryFn: getIntegrations,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    refetchOnWindowFocus: false
  });

  // Profile schema is already cached - no need to fetch

  // Get mail authentication status
  const mailAuthenticated = useSelector((state: DefaultRootState) => {
    const { mail } = state.promiseData.data.userData.authentications || {};
    return mail?.mailAuthenticated === true;
  });

  const mailType = useSelector((state: DefaultRootState) => {
    const { mail } = state.promiseData.data.userData.authentications || {};
    return mail?.mailType;
  });

  // Query for user mail count with proper authentication checks
  // Use getEmailCount utility which properly checks authentication and handles re-auth flow
  const getGmailCount = useCallback(async () => {
    // Use the getEmailCount utility which has proper authentication checks
    // and error handling including re-authentication flow
    const success = await getEmailCount();
    if (success) {
      // Return the count from Redux state after successful fetch
      const mailCount = store.getState().app.mail?.mailCount || 0;
      return { data: { messagesUnread: mailCount } };
    }
    throw new Error('Failed to get mail count: Mail not authenticated or authentication failed');
  }, []);

  useQuery({
    queryKey: ['userMailCount', mailAuthenticated, mailType, googleId],
    queryFn: getGmailCount,
    enabled: !!(mailAuthenticated && mailType === 'google' && googleId && googleToken), // Only run if mail is authenticated and we have credentials
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
    // Error handling is done by getEmailCount utility and API interceptors
    // They will show auth modal and dispatch appropriate actions on auth failures
  });

  // Get user/company context for query key
  const user = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const userId = user?.id;
  const companyId = currentCompany?.id || null;

  // Query for user stats with context-aware query key
  const getStatCounts = useCallback(async () => {
    const res = await apis.getStatCounts();
    if (res) {
      // Check if any stats exceed free account limits and dispatch PAYMENT_REQUIRED if needed
      return res;
    }
  }, []);

  useQuery({
    queryKey: ['statCount', userId, companyId], // Include context in query key for proper cache invalidation
    queryFn: getStatCounts,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    enabled: !!userId // Only fetch if we have a user ID
  });

  // Query for invitations
  const getInvitationCount = useCallback(async () => {
    const res = await apis.getInvitationCount();
    return res;
  }, []);

  useQuery({
    queryKey: ['invitations'],
    queryFn: getInvitationCount,
    staleTime: 60 * 1000 * 5,
    refetchOnWindowFocus: false,
    retry: 1
  });

  const getUserHierarchy = useCallback(async () => {
    const data = await apis.getUserHierarchy()
      .then(res => {
        if (res.data) {
          return res.data;
        } else {
          return []
        }
      })
    return data || [];
  }, []);

  useQuery({
    queryKey: ['companyMembers'],
    queryFn: getUserHierarchy,
    staleTime: 1000 * 60 * 60 * 1,
    refetchOnWindowFocus: false
  })

  return (
    <BottomSheetModalProvider>
      <AddCompanyModal />
      <DashStack.Navigator
        screenOptions={{
          headerShown: false,
          // freezeOnBlur: true, // Added freezeOnBlur to the entire DashStack
          // detachInactiveScreens: true, // CRITICAL: Force unmounting of inactive screens to prevent memory leaks
          cardStyle: {
          paddingBottom: 30,
            backgroundColor: theme ? "white" : "black",
         },
        }}
        initialRouteName="Welcome" // Set to Welcome as the initial screen
        screenListeners={() => ({
          state: (e) => {
            const lastScreenArr = e.data.state.routes;
            const lastScreenArray = lastScreenArr[lastScreenArr.length - 1];
            dispatch({ type: NAVIGATE_TAB, payload: lastScreenArray, routes: e.data.state });
          }
        })}
        >
        {/* DashStack Screens */}
        <DashStack.Screen
          name="Welcome"
          component={WelcomeScreen}
          options={{
            headerTitle: 'Welcome',
            gestureEnabled: false,
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />
        <DashStack.Screen
        name="Flows"
        component={FlowNavigator}
        />
        <DashStack.Screen
          name='Integrations'
          component={IntegrationScreen}
          options={{
            headerTitle: 'Integrations',
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />
        <DashStack.Screen
          name="Academy"
          component={AcademyNavigator}
          options={{ headerTitle: 'Academy', freezeOnBlur: true, cardStyle: { backgroundColor: theme ? "white" : "black" } }} />
        <DashStack.Screen
          name="Help"
          component={HelpScreen}
          options={{
            headerTitle: 'Help',
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />
        <DashStack.Screen
          name='ComingSoonScreen'
          component={ComingSoonScreen}
          options={{
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />
        <DashStack.Screen
          name='Invitations'
          component={InvitationsScreen}
          options={{
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />
        <DashStack.Screen
          name='Messages'
          component={MessageNavigator}
          options={{
            headerTitle: 'Messages',
            freezeOnBlur: true, // Added freezeOnBlur
          }}
        />

        {/* Other Stack Navigators as Screens within DashStack */}
        {/* Note: freezeOnBlur here applies to the *navigator itself*, not its individual screens.
             To control internal screens, you'd need to set freezeOnBlur within DataNavigator, SalesNavigator etc. */}
        <DashStack.Screen
          name="Data"
          component={DataNavigator}
          options={{
            freezeOnBlur: true, // Added freezeOnBlur for the entire DataNavigator
          }}
        />
        <DashStack.Screen
          name="Sales"
          component={SalesNavigator}
          options={{
            freezeOnBlur: true, // Added freezeOnBlur for the entire SalesNavigator
          }}
        />
        <DashStack.Screen
          name="Projects"
          component={ProjectManagementNavigator}
          options={{
            headerTitle: 'Project Management',
            freezeOnBlur: true, // Added freezeOnBlur for the entire ProjectManagementNavigator
          }}
        />
        <DashStack.Screen
          name="Reports"
          component={ReportNavigator}
          options={{
            headerTitle: 'Reports',
            freezeOnBlur: true, // Added freezeOnBlur for the entire ReportNavigator
          }}
        />
        <DashStack.Screen
        name="Community"
        component={Community}
        />
        <DashStack.Screen
          name="Calendar"
          component={CalendarStackNavigator}
          options={{
            headerTitle: 'Calendar',
            freezeOnBlur: true, // Added freezeOnBlur for the entire CalendarStackNavigator
          }}
        />
        <DashStack.Screen
          name="Users"
          component={UserNavigator}
          options={{
            gestureEnabled: false,
            headerTitle: 'Action Handler',
            freezeOnBlur: true, // Added freezeOnBlur for the entire UserNavigator
          }}
        />
        <DashStack.Screen
          name="Knowledge"
          component={KnowledgeNavigator}
          options={{
            headerTitle: 'Knowledge',
            freezeOnBlur: true, // Added freezeOnBlur for the entire KnowledgeNavigator
          }}
        />
        <DashStack.Screen
          name="Settings"
          component={SettingsNavigator}
          options={{
            headerTitle: 'Settings',
            freezeOnBlur: true, // Added freezeOnBlur for the entire SettingsNavigator
          }}
        />
        <DashStack.Screen
          name="Mail"
          component={MailNavigator}
          options={{
            headerTitle: 'Mail',
            freezeOnBlur: true, // Added freezeOnBlur for the entire MailNavigator
          }}
        />
        <DashStack.Screen
          name='Networking'
          component={NetworkNavigator}
          options={{
            freezeOnBlur: true, // Added freezeOnBlur for the entire NetworkNavigator
          }}
        />
      </DashStack.Navigator>
    </BottomSheetModalProvider>
  );
}

export default memo(DashboardStack);